import { snakeCase } from 'lodash';

import { mapCreateCustomerQuoteForm } from 'quotes/components/CustomerQuoteUpsert';
import { mapCreateVendorQuoteForm } from 'quotes/components/VendorQuoteUpsert';
import { QuoteEntityType, QuoteType } from 'quotes/enums';
import { IAddPartsToQuoteData, IBaseQuote, ICustomerQuote } from 'quotes/types';

import {
  getItemsToCopyValues,
  ICreateCustomerQuoteFromQuoteForm,
  ICreateVendorQuoteFromQuoteForm,
  ItemsToCopyApiValue,
  ItemsToCopyField,
} from './Steps/SelectItemsToCopy';

interface IProps {
  sourceQuote: IBaseQuote;
  targetQuote?: ICustomerQuote | null;
  toQuoteType: QuoteType;
  data: ICreateCustomerQuoteFromQuoteForm | ICreateVendorQuoteFromQuoteForm;
  quotePartIds?: number[];
  entityType?: QuoteEntityType;
  isAlternateVersionEnabled?: boolean;
}

export const mapAddPartsToQuoteForm = ({
  sourceQuote,
  targetQuote = null,
  toQuoteType,
  data,
  quotePartIds = [],
  entityType,
  isAlternateVersionEnabled = false,
}: IProps): IAddPartsToQuoteData => {
  const itemsToCopyOptions = getItemsToCopyValues(toQuoteType);

  const itemsToCopy = Object.keys(data).filter(
    (key) => !!data[key] && itemsToCopyOptions.includes(key as ItemsToCopyField)
  ) as ItemsToCopyField[];

  const baseQuoteMapData = {
    sourceQuoteId: sourceQuote.id,
    itemsToCopy: itemsToCopy.map(snakeCase) as ItemsToCopyApiValue[],
    ...(quotePartIds.length > 0 ? { quotePartIds } : {}),
  };

  let itemsToCopyTypes = {};

  if (itemsToCopy.includes(ItemsToCopyField.QUOTE_CUSTOMER_NOTE)) {
    itemsToCopyTypes = {
      ...itemsToCopyTypes,
      quoteCustomerNoteType: data[`${ItemsToCopyField.QUOTE_CUSTOMER_NOTE}Type`],
    };
  }

  if (itemsToCopy.includes(ItemsToCopyField.QUOTE_INTERNAL_NOTE)) {
    itemsToCopyTypes = {
      ...itemsToCopyTypes,
      quoteInternalNoteType: data[`${ItemsToCopyField.QUOTE_INTERNAL_NOTE}Type`],
    };
  }

  let extraData = {};

  if (
    !sourceQuote.isOrder &&
    QuoteType.VENDOR === sourceQuote.type &&
    QuoteType.CUSTOMER === toQuoteType
  ) {
    const createCustomerQuoteFormData = data as ICreateCustomerQuoteFromQuoteForm;

    extraData = {
      ...extraData,
      ...(createCustomerQuoteFormData.attachQuote ? { attachQuote: 1 } : {}),
    };
  }

  if (targetQuote?.id) {
    return {
      ...baseQuoteMapData,
      ...extraData,
      itemsToCopyTypes,
      targetQuote: { id: targetQuote.id },
    };
  }

  const targetQuoteMapData =
    QuoteType.VENDOR === toQuoteType
      ? mapCreateVendorQuoteForm(data as ICreateVendorQuoteFromQuoteForm, entityType).quote
      : mapCreateCustomerQuoteForm(
          data as ICreateCustomerQuoteFromQuoteForm,
          entityType,
          isAlternateVersionEnabled
        ).quote;

  return {
    ...baseQuoteMapData,
    ...extraData,
    itemsToCopyTypes,
    targetQuote: targetQuoteMapData,
  };
};
